<template>
  <div
    class="cart-drawer critical-component-hide"
    :class="{'cart-drawer--has-product-upsell' : hasLocalProducts }"
  >
    <product-upsell
      v-if="!isMobile && cart.item_count > 0"
      :liquid="liquid"
      :products="products"
      :product-count="productCount"
      :recommendations="recommendations"
      product-upsell-splide-settings-cart-drawer
      @has-local-products="handleHasLocalProducts"
    />

    <div class="cart-drawer__container">
      <p class="cart-drawer__header">
        <button
          class="cart-drawer__close icon-button"
          :aria-label="$string('accessibility.close')"
          type="button"
          @click="closeOverlay('cartDrawer')"
        >
          <icon-close />
        </button>

        <span
          class="cart-drawer__title text-heading-xl text-heading-2xl-desktop"
        >
          {{ $string('cart.title') }}
          <span
            class="cart-drawer__item-count text-body-m-regular text-body-l-regular-desktop"
          >
            ({{ $string(
              'cart.item_count',
              {
                pluralise: cart.item_count,
                replace: { count: cart.item_count },
              },
            ) }})
          </span>
        </span>
      </p>

      <transition
        name="cart-drawer-message"
        mode="out-in"
      >
        <div
          v-if="cart.item_count > 0 && showCheckoutUrgencyMessage
           && checkoutUrgencyMessage"
          key="message"
          class="cart-drawer__checkout-urgency-message"
        >
          <icon-alert-circle class="cart-drawer__checkout-urgency-icon" />
          <p class="text-body-s-regular">
            {{ checkoutUrgencyMessage }}
          </p>
        </div>
      </transition>

      <free-shipping-indicator
        v-if="cart.item_count > 0"
        class="cart-drawer__free-shipping-indicator"
        :free-shipping-threshold="freeShippingThreshold"
        display-collectors-club-membership
      />

      <form
        v-show="hasCartContent"
        class="cart-drawer__form"
        :action="`${$variable('routes.cart.url')}?locale=${$variable('store.language')}`"
        method="post"
      >
        <div
          class="cart-drawer__body"
          js-overlay="scrollElement"
        >
          <div class="cart-drawer__line-items">
            <template v-if="!isCollectorsClubMember && freeMembershipThresholdReached">
              <line-item-card
                class="cart-drawer__line-item-card"
                v-bind="freeMembershipContent"
                template="cart-drawer"
              />
              <divider-line />
            </template>

            <template
              v-for="(lineItem, index) of cartItems"
              :key="getCartItemKey(lineItem)"
            >
              <line-item
                class="cart-drawer__line-item"
                :line-item="lineItem"
                template="cart-drawer"
              />

              <divider-line v-if="(index + 1) !== cartItems.length" />
            </template>

            <divider-line v-if="isMobile" />

            <product-upsell
              v-if="isMobile"
              :liquid="liquid"
              :products="products"
              :product-count="productCount"
              :recommendations="recommendations"
              product-upsell-splide-settings-cart-drawer
              @has-local-products="handleHasLocalProducts"
            />
          </div>
        </div>

        <div class="cart-drawer__footer">
          <cart-summary
            class="cart-drawer__summary"
            template="cart-drawer"
            :free-shipping-threshold="freeShippingThreshold"
            :minimum-delivery-cost="minimumDeliveryCost"
          />

          <!-- eslint-disable vue/prefer-true-attribute-shorthand -->
          <btn
            class="cart-summary__submit"
            :label="$string('cart.checkout')"
            :show-icon="true"
            name="checkout"
            type="submit"
          >
            <template #icon>
              <icon-lock />
            </template>
          </btn>

          <btn
            class="cart-summary__basket"
            class-label="text-body-s-underline"
            :label="$string('cart.view_cart')"
            modifiers="text center"
            :url="$variable('routes.cart.url')"
          />

          <div
            ref="additionalCheckoutButtons"
            class="cart-drawer__additional-checkout-buttons"
          />

          <klarna-messaging
            v-if="((!isMobile) || (isMobile && cart.total_price >= 10000))"
            namespace="cart-drawer"
            :price="cart.total_price"
          />

          <div
            v-if="reassuranceImage || reassuranceOverlayText1 || reassuranceOverlayText2 ||
                 reassuranceText1 || reassuranceText2"
            class="cart-drawer__reassurance"
          >
            <div class="cart-drawer__reassurance-media">
              <img
                v-if="reassuranceImage"
                :src="reassuranceImage"
                :alt="reassuranceText1 || reassuranceOverlayText1"
                class="cart-drawer__reassurance-image"
              >

              <div
                v-if="reassuranceOverlayText1 || reassuranceOverlayText2"
                class="cart-drawer__reassurance-overlay"
              >
                <p
                  v-if="reassuranceOverlayText1"
                  class="cart-drawer__reassurance-overlay-text
                         cart-drawer__reassurance-overlay-text--primary
                         text-body-xl-bold text-body-l-bold-desktop"
                  v-text="reassuranceOverlayText1"
                />

                <p
                  v-if="reassuranceOverlayText2"
                  class="cart-drawer__reassurance-overlay-text
                         text-body-xs-regular text-body-m-regular-desktop"
                  v-text="reassuranceOverlayText2"
                />
              </div>
            </div>

            <div class="cart-drawer__reassurance-texts">
              <p
                v-if="reassuranceText1"
                class="cart-drawer__reassurance-text
                       text-body-xs-regular text-body-l-regular-desktop"
                v-text="reassuranceText1"
              />

              <p
                v-if="reassuranceText2"
                class="cart-drawer__reassurance-text
                       text-body-xs-regular text-body-l-regular-desktop"
                v-text="reassuranceText2"
              />
            </div>
          </div>
        </div>
      </form>

      <div
        v-show="!hasCartContent"
        class="cart-drawer__empty-container"
        js-overlay="scrollElement"
      >
        <div class="cart-drawer__empty-content">
          <p
            class="
              cart-drawer__empty-message
              text-body-s-regular
            "
            v-text="$string('cart.empty.drawer_text')"
          />

          <btn
            v-if="emptyCartCta.ctaUrl"
            class="cart-drawer__empty-cta"
            :label="emptyCartCta.ctaText"
            modifiers="primary"
            :url="emptyCartCta.ctaUrl"
          />
        </div>

        <product-upsell
          v-if="isMobile"
          :liquid="liquid"
          :products="products"
          :product-count="productCount"
          :recommendations="recommendations"
          product-upsell-splide-settings-cart-drawer
          @has-local-products="handleHasLocalProducts"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Cart drawer (cart-drawer)
 * -----------------------------------------------------------------------------
 * Displays contents of cart.
 *
 * @param {Object} overlay - Overlay API.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import CartSummary from '~async/cart-summary/cart-summary'
import DividerLine from '~async/utils/divider-line/divider-line'
import IconClose from '~icons/directional-navigation/close.svg'
import IconLock from '~icons/general/lock.svg'
import IconAlertCircle from '~icons/general/alert-circle.svg'
import KlarnaMessaging from '~async/klarna-messaging/klarna-messaging'
import LineItem from '~async/line-item/line-item'
import LineItemCard from '~async/line-item-card/line-item-card'
import FreeShippingIndicator from '~async/free-shipping-indicator/free-shipping-indicator'
import ProductUpsell from '~async/product-upsell/product-upsell'
import { moveAsyncElement } from '~/helpers/general'

export default {
  name: 'CartDrawer',

  components: {
    Btn,
    CartSummary,
    DividerLine,
    FreeShippingIndicator,
    IconClose,
    IconLock,
    IconAlertCircle,
    KlarnaMessaging,
    LineItem,
    LineItemCard,
    ProductUpsell,
  },

  props: {
    freeShippingThreshold: {
      type: Number,
      default: 0,
    },
    minimumDeliveryCost: {
      type: Number,
      default: 0,
    },
    reassuranceImageMobile: {
      type: String,
      default: '',
    },
    reassuranceImageDesktop: {
      type: String,
      default: '',
    },
    reassuranceOverlayText1: {
      type: String,
      default: 'The best for the best part.',
    },
    reassuranceOverlayText2: {
      type: String,
      default: 'Perfectly equipped from the waist down — now also with underwear.',
    },
    reassuranceText1: {
      type: String,
      default: '',
    },
    reassuranceText2: {
      type: String,
      default: '',
    },
    overlay: {
      type: Object,
      default: () => ({}),
    },
    emptyCartCta: {
      type: Object,
      default: () => ({}),
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        title: '',
      }),
      required: true,
    },
    productCount: {
      type: Number,
      default: 4,
      required: true,
    },
    products: {
      type: Object,
      default: () => ({
        count: 0,
        dynamic: [],
        section: [],
      }),
      required: true,
    },
    recommendations: {
      type: Object,
      default: () => ({
        enable: true,
        type: 'RELATED',
      }),
      required: true,
    },
    checkoutUrgencyMessage: {
      type: String,
      default: '',
    },
    checkoutUrgencyMessageDuration: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      hasLocalProducts: false,
      showCheckoutUrgencyMessage: false,
      urgencyMessageTimer: null,
    }
  },

  computed: {

    /**
     * Get the appropriate reassurance image based on screen size.
     * @returns {String}
     */
    reassuranceImage() {
      return this.isMobile ? this.reassuranceImageMobile : this.reassuranceImageDesktop
    },

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      cartItemsWithRemovals: 'cart/getCartItemsWithRemovals',
      freeMembershipThresholdReached: 'collectorsClub/freeMembershipThresholdReached',
      freeMembershipContent: 'collectorsClub/freeMembershipContent',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      cartStatus: (state) => state.cart.status,
      isCollectorsClubMember: (state) => state.collectorsClub.customer.isCollector,
      isMobile: (state) => state.index.screen.mobile,
      removedItems: (state) => state.cart.removedItems,
    }),

    /**
     * Compute if the cart has anything to display.
     * - Includes items which have been removed but can still be restored so
     *   the customer can undo the removal of their last item.
     * @returns {Boolean}
     */
    hasCartContent() {
      return (
        this.cart.item_count > 0 ||
        this.cartStatus.label === 'adding' ||
        Boolean(this.removedItems.length)
      )
    },

    /**
     * Compute total discounts for the cart drawer only.
     * @returns {Number}
     */
    totalDiscounts() {
      let savings = 0
      let discounts = 0

      this.cart.items.forEach((lineItem) => {
        if (lineItem.compare_at_line_price) {
          const difference = lineItem.compare_at_line_price - lineItem.final_line_price

          if (difference <= 0) {
            return
          }

          savings += difference
          return
        }

        discounts += lineItem.line_level_total_discount
      })

      // Cart level discounts aren't reflected in line item final prices
      const cartLevelDiscounts = (this.cart.cart_level_discount_applications || [])
        .reduce((total, application) => {
          return total + (Number(application.total_allocated_amount) || 0)
        }, 0)

      return savings + discounts + cartLevelDiscounts
    },

    /**
     * Format the displayed discount amount for the cart drawer total.
     * @returns {String}
     */
    formattedTotalDiscount() {
      return `-£${this.$formatMoney(this.totalDiscounts).replace('£', '')}`
    },

    /**
     * Compute cart items.
     * - For each item that is being added, check if it's already in the cart.
     * - Check against key (if adding from cart) or id (if adding from other).
     * - If it isn't then add a false item to act as a loading placeholder.
     * @returns {Array}
     */
    cartItems() {
      if (this.cartStatus.label !== 'adding') {
        return this.cartItemsWithRemovals
      }

      const placeholders = []

      this.cartStatus.items.forEach((addedItem) => {
        const match = this.cart.items.find((cartItem) => {
          const cartItemProperty = typeof addedItem === 'string'
            ? cartItem.key
            : cartItem.id

          return cartItemProperty === addedItem
        })

        if (match) {
          return
        }

        placeholders.push(false)
      })

      return [
        ...placeholders,
        ...this.cartItemsWithRemovals,
      ]
    },
  },

  watch: {

    /**
     * Watch cart items to update focus trap.
     */
    'cart.items'() {
      if (!this.overlay.isActive) {
        return
      }

      this.overlay.refresh({ currentElement: document.activeElement })
    },

    /**
     * Watch overlay active state to show urgency message when drawer opens.
     */
    'overlay.isActive': {
      handler(isActive) {
        if (isActive && this.cart.item_count > 0) {
          this.showCheckoutUrgencyMessage = true
          this.startUrgencyMessageTimer()
        } else {
          this.clearUrgencyMessageTimer()
          this.showCheckoutUrgencyMessage = false
        }
      },
      immediate: true,
    },
  },

  mounted() {
    moveAsyncElement(
      '[js-additional-checkout-buttons-drawer="container"]',
      this.$refs.additionalCheckoutButtons,
    )

    if (!this.overlay.isActive) {
      return
    }

    this.overlay.refresh()
  },

  beforeUnmount() {
    this.clearUrgencyMessageTimer()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
    }),

    /**
     * Get cart item key.
     * @param {Object} item - Line item.
     * @param {Number} index - Line item index.
     * @returns {String}
     */
    getCartItemKey(item, index) {
      if (!item) {
        return `cart-line-item-loading-${index}`
      }

      if (item.removed) {
        return `cart-line-item-removed-${item.key}`
      }

      return `cart-line-item-${item.key}`
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      this.overlay.open({
        component: 'cart-drawer',
        ignoreDismissed: true,
      })

      // Show urgency message when drawer is opened
      if (this.cart.item_count > 0) {
        this.showCheckoutUrgencyMessage = true
        this.startUrgencyMessageTimer()
      }
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.overlay.close()
    },

    /**
     * Handle has local products.
     * @param {Boolean} value - Whether local products exist.
     */
    handleHasLocalProducts(value = true) {
      this.hasLocalProducts = value
    },

    /**
     * Start urgency message timer to hide message after duration.
     */
    startUrgencyMessageTimer() {
      this.clearUrgencyMessageTimer()

      this.urgencyMessageTimer = setTimeout(() => {
        this.showCheckoutUrgencyMessage = false
      }, (this.checkoutUrgencyMessageDuration || 10) * 1000)
    },

    /**
     * Clear urgency message timer.
     */
    clearUrgencyMessageTimer() {
      if (this.urgencyMessageTimer) {
        clearTimeout(this.urgencyMessageTimer)
        this.urgencyMessageTimer = null
      }
    },
  },
}
</script>

<style lang="scss">
@import './cart-drawer';
</style>
